// Server-sent events reader for POST responses (EventSource only does GET). Parses `data:` lines (multi-line data
// joined with \n), ignores comments/ids/retry, and yields each event's data string.
export async function* readSSE(res: Response, signal?: AbortSignal): AsyncGenerator<{ event: string; data: string }> {
  if (!res.body) {
    // No streaming body (old WebViews): parse the whole text at once.
    yield* parseChunk(await res.text(), true).events;
    return;
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = '';
  const abort = () => { reader.cancel().catch(() => {}); };
  signal?.addEventListener('abort', abort, { once: true });
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      const out = parseChunk(buf, false);
      buf = out.rest;
      for (const e of out.events) yield e;
    }
    buf += dec.decode();
    for (const e of parseChunk(buf, true).events) yield e;
  } finally {
    signal?.removeEventListener('abort', abort);
    try { reader.releaseLock(); } catch { /* already released */ }
  }
}

function parseChunk(text: string, final: boolean): { events: { event: string; data: string }[]; rest: string } {
  const norm = text.replace(/\r\n?/g, '\n');
  const blocks = norm.split('\n\n');
  const rest = final ? '' : blocks.pop() ?? '';
  const events: { event: string; data: string }[] = [];
  for (const block of blocks) {
    if (!block.trim()) continue;
    let event = 'message';
    const data: string[] = [];
    for (const line of block.split('\n')) {
      if (!line || line.startsWith(':')) continue;
      const i = line.indexOf(':');
      const field = i < 0 ? line : line.slice(0, i);
      let value = i < 0 ? '' : line.slice(i + 1);
      if (value.startsWith(' ')) value = value.slice(1);
      if (field === 'event') event = value;
      else if (field === 'data') data.push(value);
    }
    if (data.length) events.push({ event, data: data.join('\n') });
  }
  return { events, rest };
}

/** Build an SSE body from events (used by the mock backend and tests). */
export function sseEncode(events: unknown[]): string {
  return events.map((e) => `data: ${JSON.stringify(e)}\n\n`).join('');
}
